import React, { useEffect, useMemo, useState } from 'react';
import ReactSwitch from 'react-switch';
import { cloneDeep, merge } from 'lodash';
import { Button, Checkbox, ColorPicker, Input, Select } from 'src/components';
import { CaretDown, CaretRight } from 'src/common/icons';
import { useAlerts } from 'src/common/AlertManager';
import { useUpdateAdminUserSettingsMutation } from 'src/api/mutations/adminUserSettingsMutations';

import styles from '../styles.module.scss';

interface VideoTabProps {
  userId: number;
  userSettings: any;
  adminSettings: any;
}

const RESOLUTION_GROUPS = [
  { prefix: '360p', heading: 'Low-res (360p)' },
  { prefix: '480p', heading: 'Low-res (480p)' },
  { prefix: '720p', heading: 'High-res (720p)' },
  { prefix: '1080p', heading: 'HD (1080p)' },
  { prefix: 'hls', heading: 'Adaptive (variable bandwidth)' },
];

const ASPECT_OPTIONS = [
  { label: 'Pad', value: 'pad' },
  { label: 'Preserve', value: 'preserve' },
  { label: 'Scale', value: 'scale' },
  { label: 'Crop', value: 'crop' },
];

const STORYBOARD_MODE_OPTIONS = [
  { label: 'Extract N images over entire video', value: 'number' },
  { label: 'Extract image every Nth second', value: 'second' },
  { label: 'Extract all Keyframes as images', value: 'keyframes' },
];

function groupTranscodes(transcodes: any[]) {
  const groups: { heading: string; cards: any[] }[] = [];
  for (const { prefix, heading } of RESOLUTION_GROUPS) {
    const base = transcodes.find((t) => t.key === `${prefix}-video`);
    const wm = transcodes.find((t) => t.key === `${prefix}-wm-video`);
    const cards = [base, wm].filter(Boolean);
    if (cards.length > 0) groups.push({ heading, cards });
  }
  return groups;
}

const VideoTab: React.FC<VideoTabProps> = ({ userId, userSettings, adminSettings }) => {
  const { showSuccess, showAlert } = useAlerts();
  const updateSettingsMutation = useUpdateAdminUserSettingsMutation(userId);

  const serverVideo = userSettings?.video;
  const [local, setLocal] = useState<any>(() => cloneDeep(serverVideo) || {});
  const [storyboardAdvanced, setStoryboardAdvanced] = useState(false);
  const [previewAdvanced, setPreviewAdvanced] = useState(false);
  const [transcodeAdvanced, setTranscodeAdvanced] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (serverVideo) setLocal(cloneDeep(serverVideo));
  }, [serverVideo]);

  const hasChanges = useMemo(
    () => JSON.stringify(local) !== JSON.stringify(serverVideo),
    [local, serverVideo],
  );

  const update = (patch: any) => setLocal((prev: any) => merge({}, prev, patch));

  const updateTranscode = (key: string, property: string, value: any) => {
    setLocal((prev: any) => {
      const next = cloneDeep(prev);
      if (!next.transcodes?.[key]) return next;
      next.transcodes[key][property] = value;
      return next;
    });
  };

  const handleSave = async () => {
    try {
      await updateSettingsMutation.mutateAsync({ key: 'video', value: local });
      showSuccess('Video settings saved');
    } catch {
      showAlert('Failed to save video settings');
    }
  };

  if (!serverVideo) {
    return <div className={styles.emptyState}>No video settings configured for this user.</div>;
  }

  const storyboardEnabled = local.thumbnails?.storyboard && adminSettings?.addons?.STORYBOARD?.enabled;
  const videoKeys = local.transcodes ? Object.keys(local.transcodes) : [];
  const transcodes = local.transcodes
    ? Object.entries(local.transcodes).map(([key, value]: [string, any]) => ({ key, ...value }))
    : [];
  const groups = groupTranscodes(transcodes);

  return (
    <div>
      {/* Test transcode mode */}
      <h4 className={styles.settingsSectionTitle}>Test transcode mode</h4>
      <div className={styles.onDemandImages}>
        <ReactSwitch
          checked={local.video_transcode_test_mode}
          onChange={(checked: boolean) => update({ video_transcode_test_mode: checked })}
          checkedIcon={false}
          uncheckedIcon={false}
          onColor="#2e53e3"
        />
        <p>Limit all video transcoding to 5 seconds (Free)</p>
      </div>

      {/* Video thumbnail size */}
      <h4 className={`${styles.settingsSectionTitle} ${styles.settingsSectionTitleSpaced}`}>Video thumbnail size</h4>
      <div className={styles.formCard}>
        <div className={styles.formCardGrid}>
          <div className={styles.formCardField}>
            <label>Width (PX)</label>
            <Input
              variant="secondary"
              type="number"
              value={local.thumbnails?.video_default?.width}
              onChange={(value) => update({ thumbnails: { video_default: { width: parseInt(value) || 0 } } })}
            />
          </div>
          <div className={styles.formCardField}>
            <label>Height (PX)</label>
            <Input
              variant="secondary"
              type="number"
              value={local.thumbnails?.video_default?.height}
              onChange={(value) => update({ thumbnails: { video_default: { height: parseInt(value) || 0 } } })}
            />
          </div>
        </div>
      </div>

      {/* Storyboard images */}
      {storyboardEnabled && (
        <>
          <h4 className={`${styles.settingsSectionTitle} ${styles.settingsSectionTitleSpaced}`}>Storyboard images</h4>
          <div className={styles.formCard}>
            <div className={styles.formCardHeader}>
              <span>Create on upload</span>
              <ReactSwitch
                checked={local.thumbnails.storyboard.enabled}
                onChange={(checked: boolean) => update({ thumbnails: { storyboard: { enabled: checked } } })}
                checkedIcon={false}
                uncheckedIcon={false}
                onColor="#2e53e3"
                width={40}
                height={20}
                handleDiameter={16}
              />
            </div>
            <div className={styles.formCardGrid}>
              <div className={styles.formCardField}>
                <label>Type</label>
                <Select
                  value={local.thumbnails.storyboard.mode}
                  onChange={(value: string) => update({ thumbnails: { storyboard: { mode: value } } })}
                  options={STORYBOARD_MODE_OPTIONS}
                />
              </div>
              <div className={styles.formCardField}>
                <label>Value</label>
                <Input
                  variant="secondary"
                  type="number"
                  value={local.thumbnails.storyboard.mode_value}
                  onChange={(value) => update({ thumbnails: { storyboard: { mode_value: value === '' ? 5 : parseInt(value) } } })}
                />
              </div>
            </div>
            <div className={styles.formCardGrid} style={{ gridTemplateColumns: '1fr 1fr auto' }}>
              <div className={styles.formCardField}>
                <label>Width (PX)</label>
                <Input
                  variant="secondary"
                  value={local.thumbnails.storyboard.width}
                  onChange={(value) => update({ thumbnails: { storyboard: { width: value === '' ? 1280 : parseInt(value) } } })}
                />
              </div>
              <div className={styles.formCardField}>
                <label>Height (PX)</label>
                <Input
                  variant="secondary"
                  value={local.thumbnails.storyboard.height}
                  onChange={(value) => update({ thumbnails: { storyboard: { height: value === '' ? 720 : parseInt(value) } } })}
                />
              </div>
              <div className={styles.formCardField}>
                <label>Public</label>
                <Checkbox
                  checked={local.thumbnails.storyboard.public}
                  onChange={(e) => update({ thumbnails: { storyboard: { public: e.target.checked } } })}
                />
              </div>
            </div>
            <div className={styles.advancedToggle} onClick={() => setStoryboardAdvanced((p) => !p)}>
              {storyboardAdvanced ? <CaretDown /> : <CaretRight />}
              <span>Advanced</span>
            </div>
            {storyboardAdvanced && (
              <div className={styles.advancedContent}>
                <div className={styles.formCardField}>
                  <label>Mode</label>
                  <Select
                    value={local.thumbnails.storyboard.aspect}
                    onChange={(value) => update({ thumbnails: { storyboard: { aspect: value } } })}
                    options={ASPECT_OPTIONS}
                  />
                </div>
                <div className={styles.formCardField}>
                  <label>Padding color</label>
                  <ColorPicker
                    value={local.thumbnails.storyboard.padding}
                    onChange={(color) => update({ thumbnails: { storyboard: { padding: color } } })}
                  />
                </div>
                <div className={styles.formCardField}>
                  <label>Upscale</label>
                  <Checkbox
                    checked={local.thumbnails.storyboard.upscale}
                    onChange={(e) => update({ thumbnails: { storyboard: { upscale: e.target.checked } } })}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Video Preview */}
      <h4 className={`${styles.settingsSectionTitle} ${styles.settingsSectionTitleSpaced}`}>Video Preview</h4>
      <div className={styles.formCard}>
        <div className={styles.formCardHeader}>
          <span>Create on upload</span>
          <ReactSwitch
            checked={local.preview?.video_preview_enabled}
            onChange={(checked: boolean) => update({ preview: { video_preview_enabled: checked } })}
            checkedIcon={false}
            uncheckedIcon={false}
            onColor="#2e53e3"
            width={40}
            height={20}
            handleDiameter={16}
          />
        </div>
        <div className={styles.formCardField} style={{ marginBottom: '0.5rem' }}>
          <label>Preset</label>
          <Select
            value={local.preview?.video_preview_preset}
            onChange={(value: string) => update({ preview: { video_preview_preset: value } })}
            options={videoKeys.map((key) => ({ label: key, value: key }))}
          />
        </div>
        <div className={styles.formCardGrid} style={{ gridTemplateColumns: '1fr 1fr auto' }}>
          <div className={styles.formCardField}>
            <label>Start at (second)</label>
            <Input
              variant="secondary"
              value={local.preview?.video_preview_start}
              onChange={(value: string) => update({ preview: { video_preview_start: value === '' ? 0 : parseInt(value) } })}
            />
          </div>
          <div className={styles.formCardField}>
            <label>Length (seconds)</label>
            <Input
              variant="secondary"
              value={local.preview?.video_preview_length}
              onChange={(value: string) => update({ preview: { video_preview_length: value === '' ? 0 : parseInt(value) } })}
            />
          </div>
          <div className={styles.formCardField}>
            <label>Public</label>
            <Checkbox
              checked={local.preview?.video_preview_public}
              onChange={(e) => update({ preview: { video_preview_public: e.target.checked } })}
            />
          </div>
        </div>
        <div className={styles.advancedToggle} onClick={() => setPreviewAdvanced((p) => !p)}>
          {previewAdvanced ? <CaretDown /> : <CaretRight />}
          <span>Advanced</span>
        </div>
        {previewAdvanced && (
          <div className={styles.advancedContent}>
            <div className={styles.formCardField}>
              <label>Mode</label>
              <Select
                value={local.preview?.video_preview_aspect}
                onChange={(value: string) => update({ preview: { video_preview_aspect: value } })}
                options={ASPECT_OPTIONS}
              />
            </div>
            <div className={styles.formCardField}>
              <label>Padding color</label>
              <ColorPicker
                value={local.preview?.video_preview_padding}
                onChange={(color) => update({ preview: { video_preview_padding: color } })}
              />
            </div>
            <div className={styles.formCardField}>
              <label>Upscale</label>
              <Checkbox
                checked={local.preview?.video_preview_upscale}
                onChange={(e) => update({ preview: { video_preview_upscale: e.target.checked } })}
              />
            </div>
          </div>
        )}
      </div>

      {/* Video Transcodes */}
      {groups.length > 0 && (
        <>
          <h4 className={`${styles.settingsSectionTitle} ${styles.settingsSectionTitleSpaced}`}>Video transcodes</h4>
          <p className={styles.sectionDescription}>Enable the transcode to create it when a video is uploaded</p>
          {groups.map((group) => (
            <div key={group.heading} className={styles.transcodeGroup}>
              <h5 className={styles.transcodeGroupHeading}>{group.heading}</h5>
              <div className={styles.transcodeCardGrid}>
                {group.cards.map((video) => {
                  const { key, watermark, padding, aspect, upscale, enabled, width, height, ...rest } = video;
                  const isAdvOpen = !!transcodeAdvanced[key];
                  return (
                    <div key={key} className={styles.transcodeCard}>
                      <div className={styles.transcodeCardHeader}>
                        <div className={styles.transcodeCardTitle}>
                          <label>Key</label>
                          <span className={styles.keyPill}>{key}</span>
                        </div>
                        <ReactSwitch
                          checked={enabled}
                          onChange={(checked: boolean) => updateTranscode(key, 'enabled', checked)}
                          checkedIcon={false}
                          uncheckedIcon={false}
                          onColor="#2e53e3"
                          width={40}
                          height={20}
                          handleDiameter={16}
                        />
                      </div>
                      <div className={styles.transcodeCardBody}>
                        <div className={styles.transcodeCardField}>
                          <label>Size</label>
                          <span className={styles.sizeBadge}>{width} x {height}</span>
                        </div>
                        <div className={styles.transcodeCardField}>
                          <label>Watermarked</label>
                          {key === 'hls-video' ? (
                            <Checkbox
                              checked={watermark}
                              onChange={(e) => updateTranscode(key, 'watermark', e.target.checked)}
                            />
                          ) : (
                            <span className={`${styles.wmBadge} ${watermark ? styles.wmBadgeYes : styles.wmBadgeNo}`}>
                              {watermark ? 'Yes' : 'No'}
                            </span>
                          )}
                        </div>
                        <div className={styles.transcodeCardField}>
                          <label>Public</label>
                          <Checkbox
                            checked={rest.public}
                            onChange={(e) => updateTranscode(key, 'public', e.target.checked)}
                          />
                        </div>
                      </div>
                      <div
                        className={styles.advancedToggle}
                        onClick={() => setTranscodeAdvanced((p) => ({ ...p, [key]: !p[key] }))}
                      >
                        {isAdvOpen ? <CaretDown /> : <CaretRight />}
                        <span>Advanced</span>
                      </div>
                      {isAdvOpen && (
                        <div className={styles.advancedContent}>
                          <div className={styles.transcodeCardField}>
                            <label>Mode</label>
                            <Select
                              value={aspect}
                              onChange={(value) => updateTranscode(key, 'aspect', value)}
                              options={ASPECT_OPTIONS}
                            />
                          </div>
                          <div className={styles.transcodeCardField}>
                            <label>Padding color</label>
                            <ColorPicker value={padding} onChange={(color) => updateTranscode(key, 'padding', color)} />
                          </div>
                          <div className={styles.transcodeCardField}>
                            <label>Upscale</label>
                            <Checkbox
                              checked={upscale}
                              onChange={(e) => updateTranscode(key, 'upscale', e.target.checked)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}

      <div className={styles.settingsActions}>
        <Button
          mode="primary"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || updateSettingsMutation.isLoading}
        >
          Save Video Settings
        </Button>
      </div>
    </div>
  );
};

export default VideoTab;
